import React, { useEffect, useState } from "react";
import { GitBranch, X } from "lucide-react";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { branchesService } from "@/services/branchesService";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import type { LegalEntitySummary } from "@/types/entities";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateBranchModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [legalEntities, setLegalEntities] = useState<LegalEntitySummary[]>([]);
  const [form, setForm] = useState({
    name: "",
    legalEntityId: "",
    email: "",
    workPhone: "",
    town: "",
    postcode: "",
  });

  // Load legal entities for the dropdown
  useEffect(() => {
    if (!open) return;
    legalEntitiesService
      .list({}, { page: 0, size: 100 })
      .then((res) => setLegalEntities(res.content ?? []))
      .catch(() => {});
  }, [open]);

  const { onOverlayClick } = useModalDismiss(open, onClose);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError("Branch name is required");
      return;
    }
    if (!form.legalEntityId) {
      setError("Please select a legal entity");
      return;
    }

    setLoading(true);
    try {
      await branchesService.create({
        name: form.name.trim(),
        legalEntityId: form.legalEntityId,
        email: form.email.trim() || undefined,
        workPhone: form.workPhone.trim() || undefined,
        town: form.town.trim() || undefined,
        postcode: form.postcode.trim() || undefined,
      });
      setForm({ name: "", legalEntityId: "", email: "", workPhone: "", town: "", postcode: "" });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to create branch");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm"
      onClick={onOverlayClick}
    >
      <div className="flex min-h-full items-center justify-center px-4 py-16">
        <div
          className="relative w-full max-w-lg max-h-[calc(100vh-8rem)] flex flex-col bg-bridge-surface border border-bridge-border/60 rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >

          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-bridge-border/60 rounded-t-2xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bridge-accent/10 text-bridge-accent">
                <GitBranch size={18} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-bridge-fg leading-tight">Create Branch</h2>
                <p className="text-[11px] text-bridge-muted mt-0.5 leading-tight">
                  Add a new branch to a legal entity.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="ml-3 shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-bridge-muted hover:text-bridge-fg hover:bg-white/5 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <form
            id="create-branch-modal-form"
            onSubmit={handleSubmit}
            className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4 [scrollbar-gutter:stable]"
          >
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Branch Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
                placeholder="London Central"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Legal Entity <span className="text-red-400">*</span>
              </label>
              <select
                required
                value={form.legalEntityId}
                onChange={(e) => setForm({ ...form, legalEntityId: e.target.value })}
                className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
              >
                <option value="">Select a legal entity...</option>
                {legalEntities.map((le) => (
                  <option key={le.id} value={le.id}>
                    {le.displayName ?? le.legalName ?? le.id}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
                placeholder="branch@company.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                  Phone
                </label>
                <input
                  type="tel"
                  value={form.workPhone}
                  onChange={(e) => setForm({ ...form, workPhone: e.target.value })}
                  className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
                  placeholder="+44 20 1234 5678"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                  Postcode
                </label>
                <input
                  type="text"
                  value={form.postcode}
                  onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                  className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
                  placeholder="EC1A 1BB"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Town
              </label>
              <input
                type="text"
                value={form.town}
                onChange={(e) => setForm({ ...form, town: e.target.value })}
                className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
                placeholder="London"
              />
            </div>
          </form>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-bridge-border/60 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-bridge-muted hover:text-bridge-fg bg-transparent hover:bg-white/5 border border-bridge-border/70 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-branch-modal-form"
              disabled={loading}
              className="px-5 py-2 text-sm font-bold text-white bg-bridge-accent hover:bg-bridge-accentSoft rounded-xl shadow-sm shadow-bridge-accent/20 disabled:opacity-50 transition-all active:scale-[0.98]"
            >
              {loading ? "Creating…" : "Create Branch"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
